import dayjs from 'dayjs';
import { FC } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers-pro';

import MetadataDefinitions from '@/shared/types/metadata/metadataDefinitions.type';

import useMetadataField from './hooks/useMetadataField';
import TypeDefinitionKey from './types/metadataDefinition.type';

type MetadataFieldProps = {
  metadataDefinition: MetadataDefinitions & { value: unknown };
  getFieldValue: (value: Record<string, unknown>) => void;
};

const MetadataField: FC<MetadataFieldProps> = ({
  metadataDefinition,
  getFieldValue,
}) => {
  const { metadata_key, metatype_id, required_level, value } =
    metadataDefinition;

  const {
    fields,
    onFieldChange,
    handleAddMultiCurrencyMoneyRecord,
    handleRemoveMultiCurrencyMoneyRecord,
    handleAddMultiTaxDefinitionRecord,
    handleRemoveMultiTaxDefinitionRecord,
  } = useMetadataField({ initialValue: value });

  switch (metatype_id as TypeDefinitionKey) {
    case 'boolean':
      return (
        <FormControlLabel
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          labelPlacement='end'
          control={
            <Switch
              color='primary'
              checked={fields.boolean}
              onBlur={() => getFieldValue({ [metadata_key]: fields.boolean })}
              onChange={(_, checked) =>
                onFieldChange({ value: checked, field: 'boolean' })
              }
            />
          }
        />
      );
    case 'color':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.color}
          onBlur={() => getFieldValue({ [metadata_key]: fields.color })}
          onChange={(e) =>
            onFieldChange({ value: e.target.value, field: 'color' })
          }
        />
      );
    case 'date':
      return (
        <DatePicker
          label='Controlled picker'
          value={fields.date}
          onChange={(newValue) =>
            onFieldChange({ value: dayjs(newValue), field: 'date' })
          }
          onClose={() => getFieldValue({ [metadata_key]: fields.date })}
          slotProps={{
            field: {
              onBlur: () => getFieldValue({ [metadata_key]: fields.date }),
            },
          }}
        />
      );
    case 'json':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          multiline
          rows={5}
          value={fields.json}
          onBlur={() =>
            getFieldValue({
              [metadata_key]: JSON.parse(fields.json) as Record<
                string,
                unknown
              >,
            })
          }
          onChange={(e) =>
            onFieldChange({ value: e.target.value, field: 'json' })
          }
        />
      );
    case 'money':
      return (
        <Stack direction='row' spacing={2}>
          <FormControl>
            <InputLabel id={`${metadata_key}${required_level}:currency_code`}>
              Currency Code
            </InputLabel>
            <Select
              placeholder={metadataDefinition.metadata_description}
              id={`${metadata_key}${required_level}:currency_code`}
              label='Currency Code'
              value={fields.money.currency_code}
              onBlur={() => getFieldValue({ [metadata_key]: fields.money })}
              onChange={(e) =>
                onFieldChange({
                  value: { currency_code: e.target.value },
                  field: 'money',
                })
              }
            >
              <MenuItem value='CAD'>CAD</MenuItem>
              <MenuItem value='USD'>USD</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:amount`}
            required={required_level === 'REQUIRED'}
            label='Amount'
            variant='outlined'
            value={fields.money.value}
            inputProps={{ type: 'number' }}
            onBlur={() => getFieldValue({ [metadata_key]: fields.money })}
            onChange={(e) =>
              onFieldChange({
                value: { value: e.target.value },
                field: 'money',
              })
            }
          />
        </Stack>
      );
    case 'single_line_text_field':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.single_line_text_field}
          onBlur={() =>
            getFieldValue({ [metadata_key]: fields.single_line_text_field })
          }
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'single_line_text_field',
            })
          }
        />
      );
    case 'string_integer':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.string_integer}
          inputProps={{ type: 'number' }}
          onBlur={() =>
            getFieldValue({ [metadata_key]: fields.string_integer })
          }
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'string_integer',
            })
          }
        />
      );
    case 'url':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.url}
          onBlur={() => getFieldValue({ [metadata_key]: fields.url })}
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'url',
            })
          }
        />
      );
    case 'dimension':
      return (
        <Stack direction='row' spacing={2}>
          <FormControl>
            <InputLabel id={`${metadata_key}${required_level}:unit`}>
              Unit
            </InputLabel>
            <Select
              placeholder={metadataDefinition.metadata_description}
              id={`${metadata_key}${required_level}:unit`}
              label='Unit'
              value={fields.dimension.unit}
              onBlur={() => getFieldValue({ [metadata_key]: fields.dimension })}
              onChange={(e) =>
                onFieldChange({
                  value: { unit: e.target.value },
                  field: 'dimension',
                })
              }
            >
              <MenuItem value='in'>in</MenuItem>
              <MenuItem value='mm'>mm</MenuItem>
              <MenuItem value='cm'>cm</MenuItem>
              <MenuItem value='m'>m</MenuItem>
              <MenuItem value='km'>km</MenuItem>
              <MenuItem value='ft'>ft</MenuItem>
              <MenuItem value='yd'>yd</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:value`}
            required={required_level === 'REQUIRED'}
            label='Value'
            variant='outlined'
            value={fields.dimension.value}
            inputProps={{ type: 'number' }}
            onBlur={() => getFieldValue({ [metadata_key]: fields.dimension })}
            onChange={(e) =>
              onFieldChange({
                value: { value: e.target.value },
                field: 'dimension',
              })
            }
          />
        </Stack>
      );
    case 'integer':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.integer}
          inputProps={{ type: 'number' }}
          onBlur={() => getFieldValue({ [metadata_key]: fields.integer })}
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'integer',
            })
          }
        />
      );
    case 'multi_line_text_field':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          multiline
          rows={5}
          value={fields.multi_line_text_field}
          onBlur={() =>
            getFieldValue({ [metadata_key]: fields.multi_line_text_field })
          }
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'multi_line_text_field',
            })
          }
        />
      );
    case 'numeric':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.numeric}
          inputProps={{ type: 'number' }}
          onBlur={() => getFieldValue({ [metadata_key]: fields.numeric })}
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'numeric',
            })
          }
        />
      );
    case 'rating':
      return (
        <Stack direction='row' spacing={2}>
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:scale_max`}
            required={required_level === 'REQUIRED'}
            label='Scale max'
            variant='outlined'
            value={fields.rating.scale_max}
            inputProps={{ type: 'number' }}
            onBlur={() => getFieldValue({ [metadata_key]: fields.rating })}
            onChange={(e) =>
              onFieldChange({
                value: { scale_max: e.target.value },
                field: 'rating',
              })
            }
          />
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:scale_min`}
            required={required_level === 'REQUIRED'}
            label='Scale min'
            variant='outlined'
            value={fields.rating.scale_min}
            inputProps={{ type: 'number' }}
            onBlur={() => getFieldValue({ [metadata_key]: fields.rating })}
            onChange={(e) =>
              onFieldChange({
                value: { scale_min: e.target.value },
                field: 'rating',
              })
            }
          />
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:value`}
            label='Value'
            disabled
            variant='outlined'
            value={fields.rating.value}
            onBlur={() => getFieldValue({ [metadata_key]: fields.rating })}
          />
        </Stack>
      );
    case 'rich_text_field':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          multiline
          rows={5}
          value={fields.rich_text_field}
          onBlur={() =>
            getFieldValue({ [metadata_key]: fields.rich_text_field })
          }
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'rich_text_field',
            })
          }
        />
      );
    case 'timestamp':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.timestamp}
          onBlur={() => getFieldValue({ [metadata_key]: fields.timestamp })}
          onChange={(e) =>
            onFieldChange({
              value: dayjs(e.target.value),
              field: 'timestamp',
            })
          }
        />
      );
    case 'timezone':
      return (
        <TextField
          placeholder={metadataDefinition.metadata_description}
          id={metadata_key}
          required={required_level === 'REQUIRED'}
          label={metadata_key}
          variant='outlined'
          value={fields.timezone}
          onBlur={() => getFieldValue({ [metadata_key]: fields.timezone })}
          onChange={(e) =>
            onFieldChange({
              value: e.target.value,
              field: 'timezone',
            })
          }
        />
      );
    case 'volume':
      return (
        <Stack direction='row' spacing={2}>
          <FormControl>
            <InputLabel id={`${metadata_key}${required_level}:unit`}>
              Unit
            </InputLabel>
            <Select
              placeholder={metadataDefinition.metadata_description}
              id={`${metadata_key}${required_level}:unit`}
              label='Unit'
              value={fields.volume.unit}
              onBlur={() => getFieldValue({ [metadata_key]: fields.volume })}
              onChange={(e) =>
                onFieldChange({
                  value: { unit: e.target.value },
                  field: 'volume',
                })
              }
            >
              <MenuItem value='oz'>oz</MenuItem>
              <MenuItem value='ml'>ml</MenuItem>
              <MenuItem value='l'>l</MenuItem>
              <MenuItem value='qt'>qt</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:value`}
            required={required_level === 'REQUIRED'}
            label='Value'
            variant='outlined'
            value={fields.volume.value}
            inputProps={{ type: 'number' }}
            onBlur={() => getFieldValue({ [metadata_key]: fields.volume })}
            onChange={(e) =>
              onFieldChange({
                value: { value: e.target.value },
                field: 'volume',
              })
            }
          />
        </Stack>
      );
    case 'weight':
      return (
        <Stack direction='row' spacing={2}>
          <FormControl>
            <InputLabel id={`${metadata_key}${required_level}:unit`}>
              Unit
            </InputLabel>
            <Select
              placeholder={metadataDefinition.metadata_description}
              id={`${metadata_key}${required_level}:unit`}
              label='Unit'
              value={fields.weight.unit}
              onBlur={() => getFieldValue({ [metadata_key]: fields.weight })}
              onChange={(e) =>
                onFieldChange({
                  value: { unit: e.target.value },
                  field: 'weight',
                })
              }
            >
              <MenuItem value='g'>g</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={metadataDefinition.metadata_description}
            id={`${metadata_key}${required_level}:value`}
            required={required_level === 'REQUIRED'}
            label='Value'
            variant='outlined'
            value={fields.weight.value}
            inputProps={{ type: 'number' }}
            onBlur={() => getFieldValue({ [metadata_key]: fields.weight })}
            onChange={(e) =>
              onFieldChange({
                value: { value: e.target.value },
                field: 'weight',
              })
            }
          />
        </Stack>
      );
    case 'tax_definition':
      return (
        <Stack spacing={2} alignItems='flex-start'>
          {fields.tax_definition.map((tax, index) => (
            <Stack direction='row' spacing={2} key={tax.id} alignItems='center'>
              <FormControl>
                <InputLabel
                  id={`${metadata_key}${required_level}:tax_code${index}`}
                >
                  Tax Code
                </InputLabel>
                <Select
                  fullWidth
                  placeholder={metadataDefinition.metadata_description}
                  id={`${metadata_key}${required_level}:tax_code${index}`}
                  label='Tax Code'
                  value={tax.tax_code}
                  onBlur={() =>
                    getFieldValue({
                      [metadata_key]: fields.tax_definition.map((item) => ({
                        tax_code: item.tax_code,
                        multiplier: item.multiplier,
                      })),
                    })
                  }
                  onChange={(e) =>
                    onFieldChange({
                      value: [{ ...tax, tax_code: e.target.value }],
                      field: 'tax_definition',
                    })
                  }
                >
                  <MenuItem value='GST'>GST</MenuItem>
                  <MenuItem value='PST'>PST</MenuItem>
                </Select>
              </FormControl>

              <TextField
                placeholder={metadataDefinition.metadata_description}
                id={`${metadata_key}${required_level}.multiplier${index}`}
                required={required_level === 'REQUIRED'}
                label='Multiplier'
                variant='outlined'
                value={tax.multiplier}
                inputProps={{ type: 'number' }}
                onBlur={() =>
                  getFieldValue({
                    [metadata_key]: fields.tax_definition.map((item) => ({
                      tax_code: item.tax_code,
                      multiplier: parseFloat(item.multiplier).toFixed(2),
                    })),
                  })
                }
                onChange={(e) =>
                  onFieldChange({
                    value: [{ ...tax, value: e.target.value }],
                    field: 'tax_definition',
                  })
                }
              />

              <IconButton
                onClick={() => {
                  // Todo: think about better solution
                  handleRemoveMultiTaxDefinitionRecord(
                    tax.id,
                    metadata_key,
                    getFieldValue,
                  );
                }}
                disabled={fields.tax_definition.length < 2}
              >
                <DeleteIcon sx={{ color: 'text.secondary' }} />
              </IconButton>
            </Stack>
          ))}
          <Button onClick={handleAddMultiTaxDefinitionRecord}>
            Add currency
          </Button>
        </Stack>
      );
    case 'multi_currency_money':
      return (
        <Stack spacing={2} alignItems='flex-start'>
          {fields.multi_currency_money.map((money, index) => (
            <Stack
              direction='row'
              spacing={2}
              key={money.id}
              alignItems='center'
            >
              <FormControl>
                <InputLabel
                  id={`${metadata_key}${required_level}:currency_code${index}`}
                >
                  Currency Code
                </InputLabel>
                <Select
                  fullWidth
                  placeholder={metadataDefinition.metadata_description}
                  id={`${metadata_key}${required_level}:currency_code${index}`}
                  label='Currency Code'
                  value={money.currency_code}
                  onBlur={() =>
                    getFieldValue({
                      [metadata_key]: fields.multi_currency_money.map(
                        (item) => ({
                          currency_code: item.currency_code,
                          value: item.value,
                        }),
                      ),
                    })
                  }
                  onChange={(e) =>
                    onFieldChange({
                      value: [{ ...money, currency_code: e.target.value }],
                      field: 'multi_currency_money',
                    })
                  }
                >
                  <MenuItem value='CAD'>CAD</MenuItem>
                  <MenuItem value='USD'>USD</MenuItem>
                </Select>
              </FormControl>

              <TextField
                placeholder={metadataDefinition.metadata_description}
                id={`${metadata_key}${required_level}.value${index}`}
                required={required_level === 'REQUIRED'}
                label='Value'
                variant='outlined'
                value={money.value}
                inputProps={{ type: 'number' }}
                onBlur={() =>
                  getFieldValue({
                    [metadata_key]: fields.multi_currency_money.map((item) => ({
                      currency_code: item.currency_code,
                      value: parseFloat(item.value).toFixed(2),
                    })),
                  })
                }
                onChange={(e) =>
                  onFieldChange({
                    value: [{ ...money, value: e.target.value }],
                    field: 'multi_currency_money',
                  })
                }
              />

              <IconButton
                onClick={() => {
                  // Todo: think about better solution
                  handleRemoveMultiCurrencyMoneyRecord(
                    money.id,
                    metadata_key,
                    getFieldValue,
                  );
                }}
                disabled={fields.multi_currency_money.length < 2}
              >
                <DeleteIcon sx={{ color: 'text.secondary' }} />
              </IconButton>
            </Stack>
          ))}
          <Button onClick={handleAddMultiCurrencyMoneyRecord}>
            Add currency
          </Button>
        </Stack>
      );
    default:
      throw new Error(
        'Metadata id is incorrect or was not provided in src/features/Products/ProductAdd/components/MetadataField/MetadataField.tsx',
      );
  }
};

export default MetadataField;
